import React, { useEffect, useState } from "react";
import { DownloadIcon, LucidLoader, PrinterIcon } from "../Icons";

const PDFModalViewer = ({
  show,
  handleClose,
  pdfUrl,
  fileName = "Document.pdf",
  maxRetries = 3,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !pdfUrl) return;

    let isCancelled = false;

    const checkPdf = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000); // 5s safety timeout

        const res = await fetch(pdfUrl, { method: "GET", signal: controller.signal });
        clearTimeout(timeout);

        if (res.ok && res.status !== 204) {
          if (!isCancelled) setIsReady(true);
        } else {
          throw new Error(`Received status ${res.status}`);
        }
      } catch (err) {
        if (retryCount < maxRetries) {
          console.warn(`Retrying PDF fetch (${retryCount + 1}/${maxRetries})...`);
          setTimeout(() => setRetryCount((r) => r + 1), 1500);
        } else {
          if (!isCancelled) setError("Unable to load PDF. Please try again later.");
        }
      }
    };


    checkPdf();

    return () => {
      isCancelled = true;
    };
  }, [pdfUrl, show, retryCount]);

  if (!show) return null;

  const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
    pdfUrl
  )}&embedded=true`;

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Failed to download PDF");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${fileName}.pdf`;
      link.click();

      // Clean up memory
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download the PDF. Please try again.");
    } finally {
      setLoading(false);

    }
  };

  const handlePrint = async () => {
    try {
      const response = await fetch(pdfUrl, { mode: 'cors' });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();


        // Trigger print
        iframe.contentWindow.print();
      };
    } catch (error) {
      console.error("Printing failed:", error);
      alert("Printing failed. Please try again.");
    }
  };



  return (
    <div className="fixed inset-0 z-[10000] flex flex-col bg-black/70 backdrop-blur-sm">
      {/* Header */}
      <div className="bg-gray-900 text-white flex justify-between items-center px-6 py-3">
        <span className="font-semibold text-lg truncate">{fileName}</span>
        <div className="flex items-center gap-3">
          {!loading ? <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-600 hover:bg-gray-800 transition"
          >
            <DownloadIcon className="w-4 h-4" />
            <span>Save to Downloads</span>
          </button> :
            <div

              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-600 hover:bg-gray-800 transition"
            >
              <LucidLoader />
            </div>}
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-600 hover:bg-gray-800 transition"
          >
            <PrinterIcon className="w-4 h-4" />
            <span>Print</span>
          </button>
          <button
            onClick={handleClose}
            className="px-3 py-1.5 rounded-md border border-gray-600 hover:bg-gray-800 transition"
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex-grow bg-white overflow-hidden flex items-center justify-center">
        {!pdfUrl ? (
          <div className="text-gray-500">No PDF URL provided</div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : !isReady ? (
          <div className="text-gray-500">Loading PDF... (retry {retryCount}/{maxRetries})</div>
        ) : (
          <iframe
            key={retryCount}
            title="PDF Viewer"
            src={viewerUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        )}
      </div>
    </div>
  );
};

export default PDFModalViewer;
